import type { FC, PropsWithChildren } from 'react'
import styled from '@emotion/styled'

import ColumnTrack from '@segment/matcha/components/ColumnTrack'

import ButtonText from '@segment/matcha/components/ButtonText'

import { ChevronRightIcon } from '@segment/matcha/components/Icons/Common'

import CardBase, * as Card from '@segment/matcha/recipes/Card'

import CopyTextBase, * as CopyText from '@segment/matcha/recipes/CopyText'
import SectionBase, * as Section from '@segment/matcha/recipes/Section'

import getAbsoluteUrl from '@segment/utils/getAbsoluteUrl/getAbsoluteUrl'

import { usePageConfig } from '@segment/contexts/page'

const copy = {
  title: `Resources`,
  description: `Power your AI strategy`,
}

const cards = [
  {
    title: `State of Personalization Report 2023`,
    description: `Our annual look at how attitudes, preferences, and experiences with personalization have evolved over the past year.`,
    buttonText: `Get the report`,
    url: `/state-of-personalization-report/`,
  },
  {
    title: `Ensuring your data is AI ready`,
    description: `Segment’s customer data platform (CDP) collects, cleans, and activates AI-ready data so businesses can ensure AI applications are more precise, intelligent, and impactful.`,
    buttonText: `Learn more`,
    url: `https://segment.com/blog/ensuring-your-data-is-ai-ready/`,
  },
  {
    title: `How to master customer engagement in the AI era with data`,
    description: `In this webinar, discover how Segment CDP becomes the center of your AI models to help generate intelligent responses, anticipate customer needs, and drive conversion rates like never before.`,
    buttonText: `Register now`,
    url: `https://webinars.segment.com/segment/How-to-Master-Customer-Engagement-in-the-AI-Era-with-Data/`,
  },
]

const Resources: FC<PropsWithChildren<unknown>> = () => {
  const pageConfig = usePageConfig()
  return (
    <StyledSection variant="centered">
      <Section.Copy>
        <CopyTextBase centered={true}>
          <CopyText.Heading tag="h2">{copy.title}</CopyText.Heading>
          <CopyText.Description variant="pMedium">{copy.description}</CopyText.Description>
        </CopyTextBase>
      </Section.Copy>
      <Section.Contents>
        <Body>
          <ColumnTrack columns={1} overrides={{ md: 3 }} center={true}>
            {cards.map((card) => (
              <CardBase key={card.title} href={getAbsoluteUrl(card.url, pageConfig)}>
                <Card.Body>
                  <Card.Copy>
                    <Card.Headline>{card.title}</Card.Headline>
                    <Card.Description>{card.description}</Card.Description>
                  </Card.Copy>
                  <Card.Action alignment="left">
                    <ButtonText iconEnd={ChevronRightIcon}>{card.buttonText}</ButtonText>
                  </Card.Action>
                </Card.Body>
              </CardBase>
            ))}
          </ColumnTrack>
        </Body>
      </Section.Contents>
    </StyledSection>
  )
}

const StyledSection = styled(SectionBase)`
  row-gap: 48px;

  ${Section.Wrapper} {
    ${({ theme }) => theme.breakpoints.up(`lg`)} {
      padding-top: 120px;
    }
  }
`

const Body = styled.div``

export default Resources
